import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Upload, X, Star, Image as ImageIcon, ChevronUp, ChevronDown, Sparkles } from 'lucide-react';
import { Button, Input, useToast, Card } from '@caruso/ui';
import { productsService, ProductImage, CreateProductDto } from '@/services/products.service';
import { aiService } from '@/services/ai.service';

export function ProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [brand, setBrand] = useState('');
  const [price, setPrice] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [stock, setStock] = useState('');
  const [category, setCategory] = useState('');
  const [active, setActive] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [showInStore, setShowInStore] = useState(false);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  // Format currency input
  const formatCurrency = (value: string) => {
    // Remove tudo exceto dígitos
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';

    // Converte para número e divide por 100 para obter o valor em reais
    const number = parseInt(digits) / 100;

    // Formata como moeda brasileira
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrency = (value: string): string => {
    // Remove tudo exceto dígitos
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';

    // Converte para número e divide por 100
    const number = parseInt(digits) / 100;
    return number.toString();
  };

  const handlePriceChange = (value: string, setter: (value: string) => void) => {
    const formatted = formatCurrency(value);
    setter(formatted);
  };

  const { data: product } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productsService.getById(id!),
    enabled: !!id,
  });

  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => productsService.getCategories(),
  });

  useEffect(() => {
    if (product) {
      setName(product.name);
      setDescription(product.description || '');
      setSku(product.sku || '');
      setBrand(product.brand || '');
      // Format prices when loading
      setPrice(product.price ? (product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
      setCostPrice(product.costPrice ? (product.costPrice).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
      setStock(product.stock?.toString() || '');
      setCategory(product.category || '');
      setActive(product.active);
      setFeatured(product.featured);
      setShowInStore(product.showInStore);

      // Filter only photo images (no banner)
      const photoImgs = product.images?.filter(img => img.type !== 'banner') || [];
      setImages(photoImgs);
    }
  }, [product]);

  const saveMutation = useMutation({
    mutationFn: async (data: CreateProductDto) => {
      if (id) {
        return productsService.update(id, data);
      }
      return productsService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({
        title: id ? 'Produto atualizado!' : 'Produto criado!',
        variant: 'success',
      });
      navigate('/app/produtos');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar produto',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const generateDescriptionMutation = useMutation({
    mutationFn: () => aiService.generateProductDescription({
      name,
      category,
      currentDescription: description,
      brand: brand || undefined,
      tags: undefined, // Tags não está implementado ainda
    }),
    onSuccess: (data) => {
      setDescription(data.description);
      toast({
        title: 'Descrição gerada!',
        description: 'A IA gerou uma descrição para o produto',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao gerar descrição',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const handleGenerateDescription = () => {
    if (!name || !category) {
      toast({
        title: 'Preencha os campos obrigatórios',
        description: 'Nome e categoria são necessários para gerar a descrição',
        variant: 'destructive',
      });
      return;
    }
    generateDescriptionMutation.mutate();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Limit to 5 photos
    const availableSlots = 5 - images.length;
    if (files.length > availableSlots) {
      toast({
        title: 'Limite de fotos atingido',
        description: `Você pode adicionar no máximo ${availableSlots} foto(s)`,
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      const uploadedImages = await Promise.all(
        files.map(async (file, idx) => {
          const result = await productsService.uploadImage(file);
          return {
            url: result.url,
            fileName: result.fileName,
            fileSize: result.fileSize,
            type: 'photo',
            order: images.length + idx,
            isPrimary: images.length === 0 && idx === 0,
          };
        })
      );

      setImages((prev) => [...prev, ...uploadedImages]);
      toast({
        title: 'Imagens enviadas!',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro ao enviar imagens',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageError = (index: number) => {
    // Marca a imagem como falha e remove automaticamente
    setFailedImages((prev) => new Set(prev).add(index));
    setTimeout(() => {
      handleRemoveImage(index);
      toast({
        title: 'Imagem removida',
        description: 'A imagem não pôde ser carregada e foi removida automaticamente',
        variant: 'destructive',
      });
    }, 100);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    // If we removed the primary image, make the first one primary
    if (images[index].isPrimary && newImages.length > 0) {
      newImages[0].isPrimary = true;
    }
    setImages(newImages);
  };

  const handleSetPrimary = (index: number) => {
    setImages((prev) =>
      prev.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      }))
    );
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newImages = [...images];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    // Update order
    setImages(newImages.map((img, i) => ({ ...img, order: i })));
  };

  const handleMoveDown = (index: number) => {
    if (index === images.length - 1) return;
    const newImages = [...images];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    // Update order
    setImages(newImages.map((img, i) => ({ ...img, order: i })));
  };

  const handleSave = () => {
    if (!name) {
      toast({
        title: 'Nome obrigatório',
        description: 'Preencha o nome do produto',
        variant: 'destructive',
      });
      return;
    }

    // Filter out failed images and remove extra fields before saving
    const validImages = images
      .filter((_, index) => !failedImages.has(index))
      .map(({ url, fileName, fileSize, type, order, isPrimary }) => ({
        url,
        fileName,
        fileSize,
        type,
        order,
        isPrimary,
      }));

    const data: CreateProductDto = {
      name,
      description: description || undefined,
      sku: sku || undefined,
      brand: brand || undefined,
      price: price ? parseFloat(parseCurrency(price)) : undefined,
      costPrice: costPrice ? parseFloat(parseCurrency(costPrice)) : undefined,
      stock: stock ? parseInt(stock) : undefined,
      category: category || undefined,
      active,
      featured,
      showInStore,
      images: validImages,
    };

    saveMutation.mutate(data);
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/app/produtos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-serif text-brand-gold">
            {id ? 'Editar Produto' : 'Novo Produto'}
          </h1>
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending} variant="premium">
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <h2 className="text-xl font-serif text-brand-gold mb-4">Informações Básicas</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Nome *</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Camiseta Premium"
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-brand-ivory">Descrição</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateDescription}
                      disabled={generateDescriptionMutation.isPending || !name || !category}
                      className="text-xs"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      {generateDescriptionMutation.isPending ? 'Gerando...' : 'Gerar com IA'}
                    </Button>
                  </div>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o produto..."
                    className="w-full p-3 bg-brand-smoke/50 text-brand-ivory border border-brand-gold/20 rounded-lg resize-none placeholder:text-brand-ivory/30 focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 focus:ring-offset-brand-ink"
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">SKU</label>
                    <Input
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="Ex: CAM-001"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Marca</label>
                    <Input
                      value={brand}
                      onChange={(e) => setBrand(e.target.value)}
                      placeholder="Ex: Nike"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Categoria</label>
                  <input
                    list="categories"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="Ex: Roupas"
                    className="w-full p-3 bg-brand-smoke/50 text-brand-ivory border border-brand-gold/20 rounded-lg placeholder:text-brand-ivory/30 focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 focus:ring-offset-brand-ink"
                  />
                  <datalist id="categories">
                    {categories?.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <h2 className="text-xl font-serif text-brand-gold mb-4">Preços e Estoque</h2>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Preço de Venda</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ivory/60">R$</span>
                    <Input
                      value={price}
                      onChange={(e) => handlePriceChange(e.target.value, setPrice)}
                      placeholder="0,00"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Preço de Custo</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ivory/60">R$</span>
                    <Input
                      value={costPrice}
                      onChange={(e) => handlePriceChange(e.target.value, setCostPrice)}
                      placeholder="0,00"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Estoque</label>
                  <Input
                    type="number"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                    placeholder="0"
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                </div>
              </div>

              {price && costPrice && (
                <div className="mt-4 p-3 bg-brand-ink/80 rounded-lg border border-brand-gold/20">
                  <p className="text-sm text-brand-ivory/60">
                    Margem de lucro:{' '}
                    <span className="text-brand-gold font-semibold">
                      {(() => {
                        const priceNum = parseFloat(parseCurrency(price));
                        const costNum = parseFloat(parseCurrency(costPrice));
                        if (priceNum > 0) {
                          return ((priceNum - costNum) / priceNum * 100).toFixed(1);
                        }
                        return '0.0';
                      })()}%
                    </span>
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Photos */}
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-serif text-brand-gold">Fotos do Produto</h2>
                  <p className="text-sm text-brand-ivory/60">Até 5 fotos ({images.length}/5)</p>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="grid grid-cols-3 gap-4">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="relative aspect-square bg-brand-smoke/50 rounded-lg overflow-hidden group"
                  >
                    <img 
                      src={image.url} 
                      alt="" 
                      className="w-full h-full object-cover" 
                      onError={() => handleImageError(index)}
                    />

                    {image.isPrimary && (
                      <div className="absolute top-2 left-2 bg-brand-gold text-brand-ink px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        Principal
                      </div>
                    )}

                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 bg-brand-ink/80 border-brand-gold/40 text-brand-ivory hover:bg-brand-ink hover:text-brand-gold"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 bg-brand-ink/80 border-brand-gold/40 text-brand-ivory hover:bg-brand-ink hover:text-brand-gold"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === images.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {!image.isPrimary && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetPrimary(index)}
                          className="bg-brand-ink/80 border-brand-gold/40 text-brand-ivory hover:bg-brand-ink hover:text-brand-gold"
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveImage(index)}
                        className="bg-brand-ink/80 border-brand-gold/40 text-brand-ivory hover:bg-brand-ink hover:text-brand-gold"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {images.length < 5 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="aspect-square border-2 border-dashed border-brand-gold/40 rounded-lg flex flex-col items-center justify-center gap-2 hover:bg-brand-smoke/30 transition-colors"
                  >
                    {uploading ? (
                      <>
                        <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-brand-ivory/60">Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-brand-gold" />
                        <span className="text-sm text-brand-ivory/60">Adicionar</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {images.length === 0 && (
                <div className="text-center py-8">
                  <ImageIcon className="w-12 h-12 text-brand-gold/40 mx-auto mb-2" />
                  <p className="text-brand-ivory/60 text-sm">Nenhuma foto adicionada</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <h2 className="text-xl font-serif text-brand-gold mb-4">Status</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    className="w-4 h-4 accent-brand-gold"
                  />
                  <span className="text-brand-ivory group-hover:text-brand-gold transition-colors">Produto Ativo</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={featured}
                    onChange={(e) => setFeatured(e.target.checked)}
                    className="w-4 h-4 accent-brand-gold"
                  />
                  <span className="text-brand-ivory group-hover:text-brand-gold transition-colors">Produto em Destaque</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={showInStore}
                    onChange={(e) => setShowInStore(e.target.checked)}
                    className="w-4 h-4 accent-brand-gold"
                  />
                  <span className="text-brand-ivory group-hover:text-brand-gold transition-colors">Exibir na Loja</span>
                </label>
              </div>
            </div>
          </Card>

          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <h2 className="text-xl font-serif text-brand-gold mb-4">Dicas</h2>
              <ul className="text-sm text-brand-ivory/60 space-y-2">
                <li>• Use imagens de alta qualidade</li>
                <li>• A primeira imagem será a principal</li>
                <li>• Preencha o SKU para controle</li>
                <li>• Categoria ajuda na organização</li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
